// src/components/RecentPosts.js - FIXED VERSION
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./data/supabaseClient";
import { CustomButton11, CustomButton5, CustomButton7, RefreshButton } from "./Templates";
import './RecentPosts.css?v=1.0.1';
import { FaRegPaperPlane, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaCar, FaUserAlt, FaRoute, FaBox, FaWeightHanging, FaShippingFast, FaInfoCircle, FaCarSide, FaUser, FaArrowRight, FaRedoAlt, FaEuroSign, FaStar } from "react-icons/fa";
import { useChat } from "./Chat/ChatContext";
import { format } from "date-fns";
import { useToast } from './hooks/ToastContext';
import { useTranslation } from 'react-i18next';
import { normalizeSearchTerm, createSearchPattern } from '../utils/textUtils';
import { fixRouteEndpointsInPosts } from '../utils/routeUtils';
import { styleStaticMapUrl } from '../utils/mapStyle';
import { getUserCountry } from '../utils/countryUtils';
import { FEATURES } from '../config/features';
import PostCardPagination from './PostCardPagination';
import Spinner from './common/Spinner';
import { UserTrustChip } from './common/UserBadge';
import MessageConfirmationModal from './common/MessageConfirmationModal';
import DateSelectionModal from './common/DateSelectionModal';

/**
 * RecentPosts component to display a grid of recent posts/packages with progressive loading
 */
function RecentPosts({ filters, useModals = false, onViewPost, currentUser: userProp, filterOwnPosts, applyFilters, setApplyFilters, initialLoad = false, refreshRef, onRefreshStateChange, viewMode = 'regular' }) {
  const { t } = useTranslation('common');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedPosts, setLoadedPosts] = useState(new Set());
  const [skeletonCount, setSkeletonCount] = useState(6);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  
  // Pagination state - FIXED
  const [displayedPosts, setDisplayedPosts] = useState(12); // How many posts to show
  const [totalAvailablePosts, setTotalAvailablePosts] = useState(0); // Total posts in DB
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const POSTS_PER_PAGE = 12;
  
  // Refresh functionality state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [newPostsCount, setNewPostsCount] = useState(0);
  
  const { openChatWithConversation, checkCanStartChat } = useChat();
  const [currentUser, setCurrentUser] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  // Message confirmation modal state
  const [messageConfirmModal, setMessageConfirmModal] = useState({
    isOpen: false,
    post: null,
    isLoading: false
  });

  // Date selection modal state (for recurring posts)
  const [dateSelectionModal, setDateSelectionModal] = useState({
    isOpen: false,
    post: null
  });

  const loadingQueueRef = useRef([]);
  const isLoadingRef = useRef(false);
  const BATCH_SIZE = 3;
  
  // Track if this is the first render
  const isInitialRenderRef = React.useRef(true);
  
  // Debug function to log pagination state
  const logPaginationState = () => {
    console.log('📊 Pagination Debug:', {
      displayedPosts,
      totalPosts: posts.length,
      totalAvailablePosts,
      canLoadMore: !loading && !allDataLoaded && displayedPosts < totalAvailablePosts,
      loading,
      allDataLoaded,
      isLoadingMore
    });
  };
  
  // Preload critical images
  const preloadImage = useCallback((src) => {
    if (!src || src === '/placeholder-map.jpg') return;
    const img = new Image();
    img.src = src;
  }, []);
  
  // Preload images for loaded posts
  useEffect(() => {
    posts.forEach(post => {
      if (post.loaded && post.map_image_url) {
        preloadImage(styleStaticMapUrl(post.map_image_url));
      }
    });
  }, [posts, preloadImage]);

  // Define the handleMouseMove function at component level
  const handleMouseMove = (e, container) => {
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    container.style.setProperty('--x', `${x}%`);
    container.style.setProperty('--y', `${y}%`);
  };

  // Set currentUser directly from props
  useEffect(() => {
    if (userProp) {
      setCurrentUser(userProp);
    }
  }, [userProp]);

  // Progressive loading function
  const loadPostBatch = useCallback(async (postIds, isPackage = false) => {
    try {
      const tableName = isPackage ? 'packages' : 'posts';
      
      const { data, error} = await supabase
        .from(tableName)
        .select(`
          *, 
          user_id, 
          profiles!${tableName}_user_id_fkey(
            username,
            avatar_url,
            overall_score,
            user_badges(
              badge_type,
              is_active,
              earned_at
            )
          )
        `)
        .in('id', postIds);
      
      if (error) throw error;
      
      const processedPosts = data.map(post => {
        // Extract profiles data
        const profileData = post.profiles;
        
        // Extract active badges and sort by most recent
        const userBadges = profileData?.user_badges
          ?.filter(badge => badge.is_active)
          ?.sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at))
          ?.map(badge => badge.badge_type) || [];
        
        return {
          ...post,
          isPackage,
          role: isPackage ? 'Package' : post.role,
          profiles: {
            ...profileData,
            badges: userBadges
          }
        };
      });
      
      // Apply route corrections for this batch only
      const correctedPosts = isPackage 
        ? await fixRouteEndpointsInPosts(processedPosts, true)
        : await fixRouteEndpointsInPosts(processedPosts, false);
      
      return correctedPosts;
    } catch (error) {
      console.error('Error loading post batch:', error);
      return [];
    }
  }, []);
  
  // Process loading queue
  const processLoadingQueue = useCallback(async () => {
    if (isLoadingRef.current || loadingQueueRef.current.length === 0) {
      return;
    }
    
    isLoadingRef.current = true;
    
    try {
      // Take next batch from queue
      const batch = loadingQueueRef.current.splice(0, BATCH_SIZE);
      const regularPosts = batch.filter(item => !item.isPackage);
      const packagePosts = batch.filter(item => item.isPackage);
      
      // Load batches in parallel
      const [regularResults, packageResults] = await Promise.all([
        regularPosts.length > 0 
          ? loadPostBatch(regularPosts.map(p => p.id), false)
          : [],
        packagePosts.length > 0 
          ? loadPostBatch(packagePosts.map(p => p.id), true)
          : []
      ]);
      
      const allResults = [...regularResults, ...packageResults];
      
      // Update posts and loaded status
      setPosts(prevPosts => {
        const updatedPosts = [...prevPosts];
        allResults.forEach(newPost => {
          const index = updatedPosts.findIndex(p => 
            p.id === newPost.id && p.isPackage === newPost.isPackage
          );
          if (index !== -1) {
            updatedPosts[index] = { ...newPost, loaded: true };
          }
        });
        return updatedPosts;
      });
      
      // Mark as loaded
      setLoadedPosts(prev => {
        const newSet = new Set(prev);
        allResults.forEach(post => {
          newSet.add(`${post.isPackage ? 'pkg' : 'post'}_${post.id}`);
        });
        return newSet;
      });
      
      // Continue processing if there are more items
      if (loadingQueueRef.current.length > 0) {
        setTimeout(() => processLoadingQueue(), 100);
      } else {
        // Check if we've loaded all available posts
        const allPostsLoaded = posts.length >= totalAvailablePosts && totalAvailablePosts > 0;
        setAllDataLoaded(allPostsLoaded);
        setLoading(false);
        console.log('✅ All posts loaded successfully', { postsLoaded: posts.length, totalAvailable: totalAvailablePosts });
        logPaginationState();
      }
      
    } catch (error) {
      console.error('Error processing loading queue:', error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [loadPostBatch]);

  // FIXED: Load More Posts Function
  const handleLoadMore = useCallback(async () => {
    console.log('🔄 Load More clicked');
    logPaginationState();
    
    if (isLoadingMore || loading) {
      console.log('❌ Cannot load more - already loading');
      return;
    }
    
    const remainingPosts = totalAvailablePosts - displayedPosts;
    if (remainingPosts <= 0 || displayedPosts >= totalAvailablePosts) {
      console.log('❌ No more posts to load - all posts displayed');
      setAllDataLoaded(true);
      return;
    }
    
    try {
      setIsLoadingMore(true);
      console.log(`📥 Loading ${Math.min(POSTS_PER_PAGE, remainingPosts)} more posts...`);
      
      // Calculate how many more posts to show
      const newDisplayedCount = displayedPosts + POSTS_PER_PAGE;
      
      // If we don't have enough posts loaded yet, fetch more
      if (posts.length < newDisplayedCount) {
        console.log('📡 Need to fetch more data from server...');
        await fetchMorePosts(newDisplayedCount);
      }
      
      // Update displayed posts count
      setDisplayedPosts(newDisplayedCount);
      
      console.log(`✅ Now showing ${newDisplayedCount} posts`);
      
      // Show success message
      addToast({
        type: 'success',
        title: t('posts.loaded_more'),
        description: t('posts.showing_count', { 
          showing: Math.min(newDisplayedCount, totalAvailablePosts), 
          total: totalAvailablePosts 
        })
      });
      
    } catch (error) {
      console.error('❌ Error loading more posts:', error);
      addToast({
        type: 'error',
        title: t('errors.load_more_failed'),
        description: error.message
      });
    } finally {
      setIsLoadingMore(false);
      logPaginationState();
    }
  }, [isLoadingMore, loading, displayedPosts, totalAvailablePosts, posts.length, addToast, t]);
  
  // NEW: Function to fetch additional posts from server
  const fetchMorePosts = useCallback(async (requiredCount) => {
    try {
      console.log(`🔍 Fetching up to ${requiredCount} posts from server...`);
      
      // Build posts query with country filtering
      let query = supabase
        .from("posts")
        .select("id, created_at, user_id")
        .eq("status", "active")
        .neq('user_id', currentUser.id);
      
      // Apply country filtering if multi-country is enabled
      if (FEATURES.MULTI_COUNTRY_ENABLED) {
        const userCountry = getUserCountry({ profile: currentUser });
        query = query.eq('address_from_country', userCountry);
        console.log(`🌍 Filtering posts by country: ${userCountry}`);
      }

      // Apply filters
      if (filters?.roles?.length > 0) {
        const regularRoles = filters.roles.filter(role => role !== 'Package');
        if (regularRoles.length > 0) {
          query = query.in('role', regularRoles);
        } else {
          query = query.eq('id', -1);
        }
      }

      if (filters?.from) {
        const normalizedFrom = normalizeSearchTerm(filters.from);
        query = query.or(`normalized_from_city.ilike.%${normalizedFrom}%,normalized_from_street.ilike.%${normalizedFrom}%`);
      }
      if (filters?.to) {
        const normalizedTo = normalizeSearchTerm(filters.to);
        query = query.or(`normalized_to_city.ilike.%${normalizedTo}%,normalized_to_street.ilike.%${normalizedTo}%`);
      }

      // DEFENSIVE FILTER: Always get valid recurring post IDs (those with active occurrences)
      // This prevents zombie recurring posts from appearing even if cron job fails
      let validRecurringPostIds = [];
      try {
        const { data: validOccurrences } = await supabase
          .from('post_occurrences')
          .select('parent_post_id')
          .eq('status', 'active');
        validRecurringPostIds = [...new Set(validOccurrences?.map(o => o.parent_post_id) || [])];
        console.log(`✅ Valid recurring posts (with active occurrences):`, validRecurringPostIds.length);
      } catch (err) {
        console.warn('Error fetching valid recurring posts:', err);
      }

      // Handle date filtering for both one-time and recurring posts
      if (filters?.dateFrom || filters?.dateTo) {
        const dateFromFilter = filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null;
        const dateToFilter = filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null;

        // Get recurring post IDs that have occurrences in the date range
        let recurringPostIds = [];
        try {
          let occurrenceQuery = supabase
            .from('post_occurrences')
            .select('parent_post_id')
            .eq('status', 'active');

          if (dateFromFilter) {
            occurrenceQuery = occurrenceQuery.gte('occurrence_date', dateFromFilter);
          }
          if (dateToFilter) {
            occurrenceQuery = occurrenceQuery.lte('occurrence_date', dateToFilter);
          }

          const { data: occurrences } = await occurrenceQuery;
          recurringPostIds = [...new Set(occurrences?.map(o => o.parent_post_id) || [])];
          console.log(`📅 Recurring posts with occurrences in range:`, recurringPostIds);
        } catch (err) {
          console.warn('Error fetching recurring post occurrences:', err);
        }

        // Apply filter: include posts that match date criteria
        // For recurring posts: check if ID is in recurringPostIds list
        // For one-time posts: check if travel_date is in range
        if (recurringPostIds.length > 0) {
          // Include recurring posts OR one-time posts with matching dates
          query = query.or(`id.in.(${recurringPostIds.join(',')}),and(travel_type.eq.One-time${dateFromFilter ? ',travel_date.gte.' + dateFromFilter : ''}${dateToFilter ? ',travel_date.lte.' + dateToFilter : ''})`);
        } else {
          // No recurring posts found, only filter one-time posts by date
          query = query.eq('travel_type', 'One-time');
          if (dateFromFilter) {
            query = query.gte('travel_date', dateFromFilter);
          }
          if (dateToFilter) {
            query = query.lte('travel_date', dateToFilter);
          }
        }
      } else {
        // NO date filter: Apply defensive filter to exclude recurring posts without occurrences
        if (validRecurringPostIds.length > 0) {
          // Include: one-time posts OR recurring posts that have active occurrences
          query = query.or(`travel_type.eq.One-time,id.in.(${validRecurringPostIds.join(',')})`);
        } else {
          // Safety: Only show one-time posts if no valid recurring posts found
          query = query.eq('travel_type', 'One-time');
        }
      }
      if (filters?.timeFrom) {
        query = query.gte('travel_time', filters.timeFrom.toTimeString().split(' ')[0]);
      }
      if (filters?.timeTo) {
        query = query.lte('travel_time', filters.timeTo.toTimeString().split(' ')[0]);
      }

      const { data: postsData, error } = await query
        .order("created_at", { ascending: false })
        .limit(requiredCount);
      
      if (error) throw error;
      
      let allPostIds = (postsData || []).map(post => ({ ...post, isPackage: false }));
      
      // Fetch packages if needed
      const includePackages = !filters?.roles?.length || filters.roles.includes('Package');
      if (includePackages) {
        let packageQuery = supabase
          .from("packages")
          .select("id, created_at, user_id")
          .eq("status", "active")
          .neq('user_id', currentUser.id);
        
        // Apply country filtering to packages
        if (FEATURES.MULTI_COUNTRY_ENABLED) {
          const userCountry = getUserCountry({ profile: currentUser });
          packageQuery = packageQuery.eq('address_from_country', userCountry);
          console.log(`🌍 Filtering packages by country: ${userCountry}`);
        }

        // Apply same filters to packages
        if (filters?.from) {
          const normalizedFrom = normalizeSearchTerm(filters.from);
          packageQuery = packageQuery.or(`normalized_from_city.ilike.%${normalizedFrom}%,normalized_from_street.ilike.%${normalizedFrom}%`);
        }
        if (filters?.to) {
          const normalizedTo = normalizeSearchTerm(filters.to);
          packageQuery = packageQuery.or(`normalized_to_city.ilike.%${normalizedTo}%,normalized_to_street.ilike.%${normalizedTo}%`);
        }
        if (filters?.dateFrom) {
          packageQuery = packageQuery.gte('travel_date', filters.dateFrom.toISOString().split('T')[0]);
        }
        if (filters?.dateTo) {
          packageQuery = packageQuery.lte('travel_date', filters.dateTo.toISOString().split('T')[0]);
        }
        if (filters?.timeFrom) {
          packageQuery = packageQuery.gte('travel_time', filters.timeFrom.toTimeString().split(' ')[0]);
        }
        if (filters?.timeTo) {
          packageQuery = packageQuery.lte('travel_time', filters.timeTo.toTimeString().split(' ')[0]);
        }
        
        const remainingSlots = Math.max(0, requiredCount - allPostIds.length);
        const { data: packageData, error: packageError } = await packageQuery
          .order("created_at", { ascending: false })
          .limit(remainingSlots);
        
        if (packageError) throw packageError;
        
        if (packageData?.length) {
          const packageIds = packageData.map(pkg => ({ ...pkg, isPackage: true }));
          allPostIds = [...allPostIds, ...packageIds];
        }
      }
      
      // Sort by date
      allPostIds.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      // Filter own posts if needed
      if (filterOwnPosts) {
        allPostIds = filterOwnPosts(allPostIds);
      }
      
      // Only get new posts that we don't have yet
      const existingIds = new Set(posts.map(p => `${p.isPackage ? 'pkg' : 'post'}_${p.id}`));
      const newPostIds = allPostIds.filter(p => !existingIds.has(`${p.isPackage ? 'pkg' : 'post'}_${p.id}`));
      
      if (newPostIds.length > 0) {
        console.log(`📦 Found ${newPostIds.length} new posts to add`);
        
        // Create skeleton posts for new ones
        const newSkeletonPosts = newPostIds.map(postId => ({
          id: postId.id,
          isPackage: postId.isPackage,
          loaded: false,
          created_at: postId.created_at
        }));
        
        // Add to existing posts
        setPosts(prevPosts => [...prevPosts, ...newSkeletonPosts]);
        
        // Add to loading queue
        loadingQueueRef.current = [...loadingQueueRef.current, ...newPostIds];
        
        // Start processing if not already processing
        if (!isLoadingRef.current) {
          processLoadingQueue();
        }
      }
      
    } catch (error) {
      console.error('Error fetching more posts:', error);
      throw error;
    }
  }, [currentUser, filters, filterOwnPosts, posts, processLoadingQueue]);
  
  // Check if more posts can be loaded - FIXED
  const canLoadMore = !loading && !isLoadingMore && displayedPosts < totalAvailablePosts && totalAvailablePosts > 0;

  // Function to check for new posts since last refresh
  const checkForNewPosts = useCallback(async () => {
    if (!currentUser) return 0;
    
    try {
      // Count new posts since last refresh with country filtering
      let postsQuery = supabase
        .from("posts")
        .select("id", { count: 'exact', head: true })
        .eq("status", "active")
        .neq('user_id', currentUser.id)
        .gt('created_at', lastRefreshTime.toISOString());
      
      // Apply country filtering to new posts check
      if (FEATURES.MULTI_COUNTRY_ENABLED) {
        const userCountry = getUserCountry({ profile: currentUser });
        postsQuery = postsQuery.eq('address_from_country', userCountry);
      }

      // Apply current filters to the count
      if (filters?.roles?.length > 0) {
        const regularRoles = filters.roles.filter(role => role !== 'Package');
        if (regularRoles.length > 0) {
          postsQuery = postsQuery.in('role', regularRoles);
        } else {
          postsQuery = postsQuery.eq('id', -1);
        }
      }

      if (filters?.from) {
        const normalizedFrom = normalizeSearchTerm(filters.from);
        postsQuery = postsQuery.or(`normalized_from_city.ilike.%${normalizedFrom}%,normalized_from_street.ilike.%${normalizedFrom}%`);
      }
      if (filters?.to) {
        const normalizedTo = normalizeSearchTerm(filters.to);
        postsQuery = postsQuery.or(`normalized_to_city.ilike.%${normalizedTo}%,normalized_to_street.ilike.%${normalizedTo}%`);
      }
      
      const { count: newPostsCount, error: postsError } = await postsQuery;
      if (postsError) throw postsError;

      let newPackagesCount = 0;
      const includePackages = !filters?.roles?.length || filters.roles.includes('Package');
      
      if (includePackages) {
        let packagesQuery = supabase
          .from("packages")
          .select("id", { count: 'exact', head: true })
          .eq("status", "active")
          .neq('user_id', currentUser.id)
          .gt('created_at', lastRefreshTime.toISOString());
        
        // Apply country filtering to new packages check
        if (FEATURES.MULTI_COUNTRY_ENABLED) {
          const userCountry = getUserCountry({ profile: currentUser });
          packagesQuery = packagesQuery.eq('address_from_country', userCountry);
        }

        if (filters?.from) {
          const normalizedFrom = normalizeSearchTerm(filters.from);
          packagesQuery = packagesQuery.or(`normalized_from_city.ilike.%${normalizedFrom}%,normalized_from_street.ilike.%${normalizedFrom}%`);
        }
        if (filters?.to) {
          const normalizedTo = normalizeSearchTerm(filters.to);
          packagesQuery = packagesQuery.or(`normalized_to_city.ilike.%${normalizedTo}%,normalized_to_street.ilike.%${normalizedTo}%`);
        }
        
        const { count, error: packageError } = await packagesQuery;
        if (packageError) throw packageError;
        newPackagesCount = count || 0;
      }

      const totalNewPosts = (newPostsCount || 0) + newPackagesCount;
      setNewPostsCount(totalNewPosts);
      
      if (onRefreshStateChange) {
        onRefreshStateChange({
          newPostsCount: totalNewPosts,
          isRefreshing: false,
          lastRefreshTime
        });
      }
      
      return totalNewPosts;
    } catch (error) {
      console.error('Error checking for new posts:', error);
      return 0;
    }
  }, [currentUser, lastRefreshTime, filters, onRefreshStateChange]);

  // Function to refresh the feed
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || !currentUser) return;
    
    try {
      setIsRefreshing(true);
      setNewPostsCount(0);
      
      if (onRefreshStateChange) {
        onRefreshStateChange({
          newPostsCount: 0,
          isRefreshing: true,
          lastRefreshTime
        });
      }
      
      const now = new Date();
      setLastRefreshTime(now);
      
      // Reset all states
      setLoading(true);
      setAllDataLoaded(false);
      setLoadedPosts(new Set());
      setDisplayedPosts(POSTS_PER_PAGE); // Reset to initial display count
      setTotalAvailablePosts(0);
      setIsLoadingMore(false);
      loadingQueueRef.current = [];
      
      // Re-trigger main data fetch
      isInitialRenderRef.current = true;
      
      addToast({
        type: 'success',
        title: t('posts.feed_refreshed'),
        description: t('posts.showing_latest_posts')
      });
      
    } catch (error) {
      console.error('Error refreshing feed:', error);
      addToast({
        type: 'error',
        title: t('errors.refresh_failed'),
        description: error.message
      });
    } finally {
      setIsRefreshing(false);
      
      if (onRefreshStateChange) {
        onRefreshStateChange({
          newPostsCount: 0,
          isRefreshing: false,
          lastRefreshTime: new Date()
        });
      }
    }
  }, [isRefreshing, currentUser, onRefreshStateChange, addToast, t]);

  // Periodically check for new posts
  useEffect(() => {
    if (!currentUser) return;
    
    const interval = setInterval(() => {
      if (!isRefreshing && !loading) {
        checkForNewPosts();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [currentUser, isRefreshing, loading, checkForNewPosts]);

  // Expose refresh function to parent component
  useEffect(() => {
    if (refreshRef) {
      refreshRef.current = {
        refresh: handleRefresh,
        checkForNewPosts,
        newPostsCount,
        isRefreshing
      };
    }
  }, [handleRefresh, checkForNewPosts, newPostsCount, isRefreshing, refreshRef]);

  // Main data loading effect - FIXED to not interfere with load more
  useEffect(() => {
    if (!currentUser) return;
    
    // Only fetch on initial load or when explicitly requested
    const shouldFetch = isInitialRenderRef.current || applyFilters;
    
    if (!shouldFetch) {
      console.log('⏭️ Skipping fetch - not initial load and no filter change');
      return;
    }
    
    console.log('🚀 Starting initial data fetch...');
    
    // Mark as no longer initial render
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
    }
    
    // Reset apply filters flag
    if (applyFilters && setApplyFilters) {
      setTimeout(() => setApplyFilters(false), 0);
    }
    
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setAllDataLoaded(false);
        setLoadedPosts(new Set());
        setDisplayedPosts(POSTS_PER_PAGE); // Reset to initial display count
        setTotalAvailablePosts(0);
        setIsLoadingMore(false);
        loadingQueueRef.current = [];
        
        // Get total count first with country filtering
        let countQuery = supabase
          .from("posts")
          .select("id", { count: 'exact', head: true })
          .eq("status", "active")
          .neq('user_id', currentUser.id);
        
        // Apply country filtering to count query
        if (FEATURES.MULTI_COUNTRY_ENABLED) {
          const userCountry = getUserCountry({ profile: currentUser });
          countQuery = countQuery.eq('address_from_country', userCountry);
        }
          
        // Build posts query with country filtering
        let query = supabase
          .from("posts")
          .select("id, created_at, user_id")
          .eq("status", "active")
          .neq('user_id', currentUser.id);
        
        // Apply country filtering if multi-country is enabled
        if (FEATURES.MULTI_COUNTRY_ENABLED) {
          const userCountry = getUserCountry({ profile: currentUser });
          query = query.eq('address_from_country', userCountry);
          console.log(`🌍 Filtering posts by country: ${userCountry}`);
        }

        // Apply filters to both queries
        if (filters?.roles?.length > 0) {
          const regularRoles = filters.roles.filter(role => role !== 'Package');
          if (regularRoles.length > 0) {
            countQuery = countQuery.in('role', regularRoles);
            query = query.in('role', regularRoles);
          } else {
            countQuery = countQuery.eq('id', -1);
            query = query.eq('id', -1);
          }
        }

        if (filters?.from) {
          const normalizedFrom = normalizeSearchTerm(filters.from);
          const fromFilter = `normalized_from_city.ilike.%${normalizedFrom}%,normalized_from_street.ilike.%${normalizedFrom}%`;
          countQuery = countQuery.or(fromFilter);
          query = query.or(fromFilter);
        }
        if (filters?.to) {
          const normalizedTo = normalizeSearchTerm(filters.to);
          const toFilter = `normalized_to_city.ilike.%${normalizedTo}%,normalized_to_street.ilike.%${normalizedTo}%`;
          countQuery = countQuery.or(toFilter);
          query = query.or(toFilter);
        }

        // Handle date filtering for both one-time and recurring posts
        if (filters?.dateFrom || filters?.dateTo) {
          const dateFromFilter = filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null;
          const dateToFilter = filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null;

          // Get recurring post IDs that have occurrences in the date range
          let recurringPostIds = [];
          try {
            let occurrenceQuery = supabase
              .from('post_occurrences')
              .select('parent_post_id')
              .eq('status', 'active');

            if (dateFromFilter) {
              occurrenceQuery = occurrenceQuery.gte('occurrence_date', dateFromFilter);
            }
            if (dateToFilter) {
              occurrenceQuery = occurrenceQuery.lte('occurrence_date', dateToFilter);
            }

            const { data: occurrences } = await occurrenceQuery;
            recurringPostIds = [...new Set(occurrences?.map(o => o.parent_post_id) || [])];
            console.log(`📅 Found ${recurringPostIds.length} recurring posts with occurrences in date range`);
          } catch (err) {
            console.warn('Error fetching recurring post occurrences:', err);
          }

          // Apply date filter for both count and data queries
          if (recurringPostIds.length > 0) {
            // Include recurring posts OR one-time posts with matching dates
            const orCondition = `id.in.(${recurringPostIds.join(',')}),and(travel_type.eq.One-time${dateFromFilter ? ',travel_date.gte.' + dateFromFilter : ''}${dateToFilter ? ',travel_date.lte.' + dateToFilter : ''})`;
            countQuery = countQuery.or(orCondition);
            query = query.or(orCondition);
          } else {
            // No recurring posts found, only filter one-time posts by date
            countQuery = countQuery.eq('travel_type', 'One-time');
            query = query.eq('travel_type', 'One-time');
            if (dateFromFilter) {
              countQuery = countQuery.gte('travel_date', dateFromFilter);
              query = query.gte('travel_date', dateFromFilter);
            }
            if (dateToFilter) {
              countQuery = countQuery.lte('travel_date', dateToFilter);
              query = query.lte('travel_date', dateToFilter);
            }
          }
        }
        if (filters?.timeFrom) {
          const timeFromFilter = filters.timeFrom.toTimeString().split(' ')[0];
          countQuery = countQuery.gte('travel_time', timeFromFilter);
          query = query.gte('travel_time', timeFromFilter);
        }
        if (filters?.timeTo) {
          const timeToFilter = filters.timeTo.toTimeString().split(' ')[0];
          countQuery = countQuery.lte('travel_time', timeToFilter);
          query = query.lte('travel_time', timeToFilter);
        }

        // Get total posts count
        const { count: postsCount, error: countError } = await countQuery;
        if (countError) console.warn('Error getting posts count:', countError);

        // Get initial batch of posts
        const { data: postsData, error } = await query
          .order("created_at", { ascending: false })
          .limit(displayedPosts * 2); // Fetch more than displayed to have buffer
        if (error) throw error;
        
        let allPostIds = (postsData || []).map(post => ({ ...post, isPackage: false }));
        
        // Fetch packages
        const includePackages = !filters?.roles?.length || filters.roles.includes('Package');
        let packagesCount = 0;
        
        if (includePackages) {
          let packageCountQuery = supabase
          .from("packages")
          .select("id", { count: 'exact', head: true })
          .eq("status", "active")
          .neq('user_id', currentUser.id);
        
        // Apply country filtering to package count
        if (FEATURES.MULTI_COUNTRY_ENABLED) {
          const userCountry = getUserCountry({ profile: currentUser });
          packageCountQuery = packageCountQuery.eq('address_from_country', userCountry);
        }
            
          let packageQuery = supabase
            .from("packages")
            .select("id, created_at, user_id")
            .eq("status", "active")
            .neq('user_id', currentUser.id);

          // Apply filters to package queries
          if (filters?.from) {
            const normalizedFrom = normalizeSearchTerm(filters.from);
            const fromFilter = `normalized_from_city.ilike.%${normalizedFrom}%,normalized_from_street.ilike.%${normalizedFrom}%`;
            packageCountQuery = packageCountQuery.or(fromFilter);
            packageQuery = packageQuery.or(fromFilter);
          }
          if (filters?.to) {
            const normalizedTo = normalizeSearchTerm(filters.to);
            const toFilter = `normalized_to_city.ilike.%${normalizedTo}%,normalized_to_street.ilike.%${normalizedTo}%`;
            packageCountQuery = packageCountQuery.or(toFilter);
            packageQuery = packageQuery.or(toFilter);
          }
          if (filters?.dateFrom) {
            const dateFromFilter = filters.dateFrom.toISOString().split('T')[0];
            packageCountQuery = packageCountQuery.gte('travel_date', dateFromFilter);
            packageQuery = packageQuery.gte('travel_date', dateFromFilter);
          }
          if (filters?.dateTo) {
            const dateToFilter = filters.dateTo.toISOString().split('T')[0];
            packageCountQuery = packageCountQuery.lte('travel_date', dateToFilter);
            packageQuery = packageQuery.lte('travel_date', dateToFilter);
          }
          if (filters?.timeFrom) {
            const timeFromFilter = filters.timeFrom.toTimeString().split(' ')[0];
            packageCountQuery = packageCountQuery.gte('travel_time', timeFromFilter);
            packageQuery = packageQuery.gte('travel_time', timeFromFilter);
          }
          if (filters?.timeTo) {
            const timeToFilter = filters.timeTo.toTimeString().split(' ')[0];
            packageCountQuery = packageCountQuery.lte('travel_time', timeToFilter);
            packageQuery = packageQuery.lte('travel_time', timeToFilter);
          }
          
          // Get package count
          const { count: packageCountResult, error: packageCountError } = await packageCountQuery;
          if (packageCountError) {
            console.warn('Error getting packages count:', packageCountError);
          } else {
            packagesCount = packageCountResult || 0;
          }
          
          // Get remaining slots for packages after posts
          const remainingSlots = Math.max(0, (displayedPosts * 2) - allPostIds.length);
          
          const { data: packageData, error: packageError } = await packageQuery
            .order("created_at", { ascending: false })
            .limit(remainingSlots);
          if (packageError) throw packageError;
          
          if (packageData?.length) {
            const packageIds = packageData.map(pkg => ({
              ...pkg,
              isPackage: true
            }));
            allPostIds = [...allPostIds, ...packageIds];
          }
        }
        
        // Set total available posts count
        const totalCount = (postsCount || 0) + packagesCount;
        setTotalAvailablePosts(totalCount);
        console.log(`📊 Total posts available: ${totalCount}`);
        
        // Sort and filter
        allPostIds.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        if (filterOwnPosts) {
          allPostIds = filterOwnPosts(allPostIds);
        }

        // No posts at all — surface the empty state immediately instead of
        // waiting out the 8s safety timeout (loading stayed true because the
        // fast-path completion check is gated on totalAvailablePosts > 0).
        if (allPostIds.length === 0) {
          setPosts([]);
          setLoading(false);
          setAllDataLoaded(true);
          console.log('📭 No posts available — showing empty state immediately');
          return;
        }

        // Create skeleton posts immediately
        const skeletonPosts = allPostIds.map(postId => ({
          id: postId.id,
          isPackage: postId.isPackage,
          loaded: false,
          created_at: postId.created_at
        }));
        
        setPosts(skeletonPosts);
        setSkeletonCount(Math.min(skeletonPosts.length, displayedPosts));
        
        console.log(`📦 Created ${skeletonPosts.length} skeleton posts, showing ${displayedPosts}`);
        
        // Add to loading queue and start processing
        loadingQueueRef.current = [...allPostIds];
        processLoadingQueue();
        
        logPaginationState();
        
      } catch (error) {
        console.error("❌ Error fetching initial data:", error.message);
        setPosts([]);
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [currentUser, applyFilters, processLoadingQueue, filterOwnPosts, displayedPosts, filters]);

  useEffect(() => {
    const handleThemeChange = () => {
      setRefreshTrigger(prev => !prev);
    };

    const root = document.querySelector(':root');
    root.addEventListener('change', handleThemeChange);

    return () => {
      root.removeEventListener('change', handleThemeChange);
    };
  }, []);

  const formatDate = (date) => {
    if (date) {
      return format(new Date(date), "dd/MM/yyyy");
    }
    return t('posts.not_specified');
  };

  const formatTime = (time) => {
    if (!time) return t('posts.not_specified');
    return time.split(':').slice(0, 2).join(':');
  };

  const formatCompactAddress = (street, city, country) => {
    let streetName = '';
    if (street) {
      const mainPart = street.split(',')[0];
      const parts = mainPart.split(' ');
      if (parts.length > 1 && !isNaN(parts[0])) {
        streetName = parts.slice(1).join(' ');
      } else {
        streetName = mainPart;
      }
    }

    if (city) {
      return streetName ? `${city}, ${streetName}` : city;
    } else {
      return streetName || country || 'Unknown';
    }
  };

  // Format recurrence pattern for display
  const formatRecurrencePattern = (post) => {
    if (post.travel_type !== 'recurring' || !post.recurrence_pattern) {
      return null;
    }

    const pattern = typeof post.recurrence_pattern === 'string'
      ? JSON.parse(post.recurrence_pattern)
      : post.recurrence_pattern;

    if (!pattern || !pattern.weekdays || pattern.weekdays.length === 0) {
      return null;
    }

    const weekdays = pattern.weekdays.sort((a, b) => a - b);
    const weekdayNames = {
      1: t('weekdays.short_monday') || 'Mo',
      2: t('weekdays.short_tuesday') || 'Tu',
      3: t('weekdays.short_wednesday') || 'We',
      4: t('weekdays.short_thursday') || 'Th',
      5: t('weekdays.short_friday') || 'Fr',
      6: t('weekdays.short_saturday') || 'Sa',
      7: t('weekdays.short_sunday') || 'Su'
    };

    // Check for special patterns
    if (weekdays.length === 7) {
      return t('recurring.every_day');
    }
    if (weekdays.length === 5 && weekdays.every(d => d >= 1 && d <= 5)) {
      // Show only first and last day abbreviations (Pr-Pt for Mon-Fri)
      return `${weekdayNames[1]}-${weekdayNames[5]}`;
    }
    if (weekdays.length === 2 && weekdays.includes(6) && weekdays.includes(7)) {
      // Show only first and last day abbreviations (Se-Sv for Sat-Sun)
      return `${weekdayNames[6]}-${weekdayNames[7]}`;
    }

    // Otherwise, show day abbreviations
    return weekdays.map(d => weekdayNames[d]).join(', ');
  };

  // Open confirmation modal for message
  const handleMessageClick = (post) => {
    // For recurring posts (not packages), show date selection modal first
    if (!post.isPackage && post.travel_type === 'recurring') {
      setDateSelectionModal({
        isOpen: true,
        post: post
      });
    } else {
      // For one-time posts or packages, show regular confirmation modal
      setMessageConfirmModal({
        isOpen: true,
        post: post,
        isLoading: false
      });
    }
  };

  // Handle date selection for recurring posts
  const handleDateSelected = (occurrenceData) => {
    const post = dateSelectionModal.post;
    if (!post) return;

    // Close date selection modal
    setDateSelectionModal({
      isOpen: false,
      post: null
    });

    // Open message confirmation modal with selected occurrence
    setMessageConfirmModal({
      isOpen: true,
      post: {
        ...post,
        selectedOccurrenceId: occurrenceData.occurrenceId,
        selectedOccurrenceDate: occurrenceData.date,
        selectedOccurrenceTime: occurrenceData.time
      },
      isLoading: false
    });
  };

  // Handle confirmed message send
  const handleConfirmMessage = async () => {
    const post = messageConfirmModal.post;
    if (!post) return;

    try {
      // Set loading state
      setMessageConfirmModal(prev => ({
        ...prev,
        isLoading: true
      }));

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error("Error fetching user:", userError.message);
        return;
      }
  
      if (!user) {
        addToast({
          type: 'error',
          title: t('ride_actions.error'),
          description: t('chat.error_not_authenticated')
        });
        return;
      }
      
      let result;
      let rpcError;
      
      if (post.isPackage) {
        const response = await supabase.rpc(
          'start_package_conversation',
          {
            p_user_id: user.id,
            p_other_user_id: post.user_id,
            p_package_id: post.id,
            p_message_text: null
          }
        );
        result = response.data;
        rpcError = response.error;
      } else {
        const response = await supabase.rpc(
          'start_post_conversation',
          {
            p_user_id: user.id,
            p_other_user_id: post.user_id,
            p_post_id: parseInt(post.id, 10),
            p_message_text: null,
            p_occurrence_id: post.selectedOccurrenceId || null  // Pass occurrence_id for recurring posts
          }
        );
        result = response.data;
        rpcError = response.error;
      }

      if (rpcError) {
        console.error('Error from conversation function:', rpcError);
        throw rpcError;
      }

      // Handle monthly limit reached case (not an error, just a limit)
      if (result?.error === 'monthly_chat_limit_reached') {
        console.log('Monthly chat limit reached:', result);

        // Close modal
        setMessageConfirmModal({
          isOpen: false,
          post: null,
          isLoading: false
        });

        // Show limit reached toast
        if (addToast) {
          addToast({
            type: 'warning',
            title: t('chat.limit_reached_title') || 'Chat Limit Reached',
            description: t('chat.limit_reached_message', { max: result.monthly_limit }) || t('chat.monthly_limit_reached')
          });
        }
        return; // Exit gracefully without throwing error
      }

      if (!result?.conversation_id) {
        const errorMessage = result?.error || 'Failed to create conversation';
        console.error('Failed to create conversation:', errorMessage);
        throw new Error(errorMessage);
      }

      // Close modal before showing toasts and opening chat
      setMessageConfirmModal({
        isOpen: false,
        post: null,
        isLoading: false
      });

      if (result.is_new && addToast) {
        if (result.remaining_chats <= 2 && result.monthly_limit < 9999) {
          addToast({
            type: 'warning',
            title: t('chat.low_remaining_title'),
            description: t('chat.remaining_chats', { count: result.remaining_chats })
          });
        } else {
          addToast({
            type: 'new_conversation',
            title: t('chat.conversation_started'),
            description: `${t('chat.started_conversation')} ${post.role || 'Package'} ${t('ride_actions.ride_from')} ${post.address_from_city} ${t('ride_actions.to')} ${post.address_to_city}`
          });
        }
      }

      if (result.is_new) {
        // Fetch sender's username for the notification
        const { data: senderProfile } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', user.id)
          .single();

        await supabase
          .from('notifications')
          .insert({
            user_id: post.user_id,
            type: 'new_conversation',
            message_template: 'notifications.new_conversation',
            message_params: {
              sender_name: senderProfile?.username || 'Someone'
            },
            related_id: result.conversation_id,
            sender_id: user.id,
            actor_id: user.id,
            created_at: new Date().toISOString(),
            is_read: false
          });
      }

      // Pass occurrence context if this is a recurring post
      const occurrenceData = post.selectedOccurrenceId ? {
        occurrenceId: post.selectedOccurrenceId,
        // We could fetch the occurrence date from the database if needed
        // For now, we'll let ChatV2 fetch it when displaying
      } : null;

      openChatWithConversation(result.conversation_id, occurrenceData);

      if (result.remaining_chats !== undefined) {
        const chatCountEvent = new CustomEvent('chatCountUpdated', {
          detail: {
            remaining: result.remaining_chats,
            limit: result.monthly_limit,
            count: result.monthly_count
          }
        });
        window.dispatchEvent(chatCountEvent);
      }

    } catch (error) {
      console.error("Error starting conversation:", error);
      
      // Close modal and show error
      setMessageConfirmModal({
        isOpen: false,
        post: null,
        isLoading: false
      });

      if (addToast) {
        addToast({
          type: 'error',
          title: t('ride_actions.error'),
          description: t('chat.error_conversation_start')
        });
      }
    }
  };

  // Handle modal close
  const handleCloseMessageModal = () => {
    if (messageConfirmModal.isLoading) return; // Prevent closing during loading
    
    setMessageConfirmModal({
      isOpen: false,
      post: null,
      isLoading: false
    });
  };

  const renderSkeletonCard = (index) => {
    return (
      <div className={`rp-post-card rp-skeleton ${viewMode === 'compact' ? 'rp-compact-view' : 'rp-regular-view'}`} key={`skeleton-${index}`}>
        <div className="RP-p-user-name skeleton-animate">
          <div className="rp-p-avatar skeleton-avatar"></div>
          <div className="skeleton-text"></div>
        </div>
        <div className="map-container-static skeleton-animate"></div>
        <div className="post-content">
          <div className="skeleton-text"></div>
          <div className="skeleton-text"></div>
          <div className="post-info-grid">
            <div className="skeleton-text"></div>
            <div className="skeleton-text"></div>
          </div>
          <div className="post-actions">
            <div className="skeleton-text" style={{ width: '100px', marginLeft: 'auto' }}></div>
          </div>
        </div>
      </div>
    );
  };
  
  // FIXED: Render only displayed posts
  const renderProgressiveContent = () => {
    console.log(`🎨 Rendering ${displayedPosts} out of ${posts.length} posts (total available: ${totalAvailablePosts})`);
    
    // Calculate actual posts to show - don't exceed what's available
    const actualPostsToShow = Math.min(displayedPosts, totalAvailablePosts, posts.length);
    const postsToShow = posts.slice(0, actualPostsToShow);
    const items = [];
    
    postsToShow.forEach((post, index) => {
      const isLoaded = post && post.loaded;
      const loadingKey = post ? `${post.isPackage ? 'pkg' : 'post'}_${post.id}` : null;
      const isInLoadedSet = loadingKey && loadedPosts.has(loadingKey);
      
      if (isLoaded || isInLoadedSet) {
        // Show real content
        if (post.isPackage) {
          items.push(renderPackagePost(post));
        } else {
          items.push(renderRegularPost(post));
        }
      } else {
        // Show skeleton only if we're still loading this post
        items.push(renderSkeletonCard(index));
      }
    });
    
    // Only add skeleton cards if we're still loading AND have more posts to load
    const isStillLoading = loading && !allDataLoaded && posts.length < totalAvailablePosts;
    const skeletonsNeeded = isStillLoading ? Math.max(0, Math.min(displayedPosts - postsToShow.length, totalAvailablePosts - postsToShow.length)) : 0;
    
    for (let i = 0; i < skeletonsNeeded; i++) {
      items.push(renderSkeletonCard(postsToShow.length + i));
    }
    
    return (
      <div className={`rp-posts-grid ${viewMode}-mode`}>
        {items}
        {isStillLoading && (
          <div className="loading-more-container">
            <Spinner size="small" showText={false} />
          </div>
        )}
      </div>
    );
  };

  const handlePostClick = (post) => {
    if (useModals && onViewPost) {
      onViewPost(post.id, post.isPackage ? 'package' : 'post');
    } else {
      if (post.isPackage) {
        navigate(`/package/${post.id}`);
      } else {
        navigate(`/post/${post.id}`);
      }
    }
  };

  // List mode — one post per row, full width: mini route map on the left, dense
  // details on the right. Shared by both ride and package posts.
  const renderListCard = (post) => {
    const isOwnPost = currentUser?.id === post.user_id;
    const isPackage = post.isPackage;
    const isDriver = post.role === 'Driver';
    const price = post.price || post.delivery_cost;
    const roleClass = isPackage
      ? 'rp-package-post'
      : (isDriver ? 'rp-driver-post' : 'rp-passenger-post');

    return (
      <div key={post.id} className="rp-post-frame">
        <div
          className={`rp-post-card rp-list-view ${roleClass} ${isOwnPost ? 'rp-own-post' : ''}`}
          onClick={() => handlePostClick(post)}
        >
          {/* Left: mini route map */}
          <div className="rp-list-map">
            <img
              src={styleStaticMapUrl(post.map_image_url) || '/placeholder-map.jpg'}
              alt={t('chat.route_map')}
              className="route-map"
            />
          </div>

          {/* Right: details */}
          <div className="rp-list-body">
            <div className="rp-list-top">
              <img
                src={post.profiles?.avatar_url || '/default-avatar.png'}
                alt={post.profiles?.username || t('chat.unknown_user')}
                className="rp-list-avatar"
              />
              <span className="rp-list-name">{post.profiles?.username || t('chat.unknown_user')}</span>
              <span className="rp-list-role" data-tooltip={isPackage ? t('posts.package') : post.role}>
                {isPackage
                  ? <FaBox className="badge-icon package" />
                  : (isDriver ? <FaCarSide className="badge-icon driver" /> : <FaUser className="badge-icon passenger" />)}
              </span>
              {post.travel_type === 'recurring' && (
                <span className="rp-list-role rp-recurring-badge" data-tooltip={t('forms.daily_ride') || 'Recurring'}>
                  <FaRedoAlt className="badge-icon recurring" />
                </span>
              )}
              {price && (
                <span className="rp-list-price">€{parseFloat(price).toFixed(0)}</span>
              )}
            </div>

            <div className="rp-list-route">
              <span className="rp-list-city from">{post.address_from_city || '—'}</span>
              <FaArrowRight className="rp-list-arrow" />
              <span className="rp-list-city to">{post.address_to_city || '—'}</span>
            </div>

            <div className="rp-list-meta">
              <span className="rp-list-meta-item">
                <FaCalendarAlt />
                {post.travel_type === 'recurring' ? formatRecurrencePattern(post) : formatDate(post.travel_date)}
              </span>
              <span className="rp-list-meta-item">
                <FaClock />
                {formatTime(post.travel_time)}
              </span>
            </div>
          </div>

          {/* Action */}
          <div className="rp-list-action" onClick={(e) => e.stopPropagation()}>
            {isOwnPost ? (
              <span className="rp-own-post-badge">{t('posts.your_post')}</span>
            ) : (
              <button
                type="button"
                className="rp-list-msg"
                aria-label={t('chat.send_message')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessageClick(post);
                }}
              >
                <FaRegPaperPlane size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPackagePost = (post) => {
    if (viewMode === 'list') return renderListCard(post);
    const isOwnPost = currentUser?.id === post.user_id;

    // Compact mode - no pagination, simple layout
    if (viewMode === 'compact') {
      return (
        <div key={post.id} className="rp-post-frame">
        <div
          className={`rp-post-card rp-package-post rp-compact-view ${isOwnPost ? 'rp-own-post' : ''}`}
          onClick={() => handlePostClick(post)}
        >
          {/* Simple header */}
          <div className="RP-p-user-name">
            <img
              src={post.profiles?.avatar_url || '/default-avatar.png'}
              alt={post.profiles?.username || t('chat.unknown_user')}
              className="rp-p-avatar"
            />
            <div className="user-info-with-badges">
              {/* User badges - small size for compact view */}
              {post.profiles?.badges?.length > 0 && (
                <div className="user-badges-container compact">
                  <UserTrustChip badges={post.profiles.badges} />
                </div>
              )}
              <h3>{post.profiles?.username || t('chat.unknown_user')}</h3>
            </div>
            <div className="post-badges-row">
              <div className="post-type-badge rp-package-badge" data-tooltip="Package">
                <FaBox className="badge-icon package" />
              </div>
              {/* Price Badge for packages */}
              {post.delivery_cost && (
                <div className="post-type-badge price-badge" data-tooltip={t('posts.delivery_cost_offered')}>
                  €{parseFloat(post.delivery_cost).toFixed(0)}
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="map-container-static">
            <div 
              className="map-container" 
              onMouseMove={(e) => {
                e.stopPropagation();
                handleMouseMove(e, e.currentTarget);
              }}
            >
              <img
                src={styleStaticMapUrl(post.map_image_url) || '/placeholder-map.jpg'}
                alt={t('chat.route_map')}
                className="route-map"
              />
            </div>
          </div>

          {/* Minimal content */}
          <div className="post-content">
            <div className="post-info-grid">
              <div className="post-detail">
                <span className="detail-icon"><FaCalendarAlt /></span>
                <div className="detail-content">
                  <div className="detail-label">{post.travel_type === 'recurring' ? t('recurring.schedule') : t('posts.date')}:</div>
                  <div>{post.travel_type === 'recurring' ? formatRecurrencePattern(post) : formatDate(post.travel_date)}</div>
                </div>
              </div>

              <div className="post-detail">
                <span className="detail-icon"><FaClock /></span>
                <div className="detail-content">
                  <div className="detail-label">{t('posts.time')}:</div>
                  <div>{formatTime(post.travel_time)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Simple footer */}
          <div className="post-actions">
            {isOwnPost ? (
              <span className="rp-own-post-badge">{t('posts.your_post')}</span>
            ) : (
              <CustomButton7
                label={t('chat.send_message')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessageClick(post);
                }}
              >
                <FaRegPaperPlane size={18} />
              </CustomButton7>
            )}
          </div>
        </div>
        </div>
      );
    }

    // Regular mode - with pagination
    return (
      <div key={post.id} className="rp-post-frame">
      <div
        className={`rp-post-card rp-package-post rp-regular-view ${isOwnPost ? 'rp-own-post' : ''}`}
      >
        <PostCardPagination
          userId={post.user_id}
          username={post.profiles?.username || t('chat.unknown_user')}
          post={post}
          paginated={false}
          headerContent={
            <div className="pagination-header-content" onClick={() => handlePostClick(post)}>
              <div className="RP-p-user-name">
                <img
                  src={post.profiles?.avatar_url || '/default-avatar.png'}
                  alt={post.profiles?.username || t('chat.unknown_user')}
                  className="rp-p-avatar"
                />
                <div className="user-info-with-badges">
                  {/* User badges - medium size for regular view */}
                  {post.profiles?.badges?.length > 0 && (
                    <div className="user-badges-container regular">
                      <UserTrustChip badges={post.profiles.badges} />
                    </div>
                  )}
                  <div className="rp-name-row">
                    <h3>{post.profiles?.username || t('chat.unknown_user')}</h3>
                    {Number(post.profiles?.overall_score) > 0 && (
                      <span className="rp-rating-chip" title={t('user_stats.overall')}>
                        <FaStar /> {Number(post.profiles.overall_score).toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="post-badges-row">
                <div className="post-type-badge rp-package-badge" data-tooltip="Package">
                  <FaBox className="badge-icon package" />
                </div>
                {/* Price Badge for packages */}
                {post.delivery_cost && (
                  <div className="post-type-badge price-badge" data-tooltip={t('posts.delivery_cost_offered')}>
                    €{parseFloat(post.delivery_cost).toFixed(0)}
                  </div>
                )}
              </div>
            </div>
          }
          footerContent={
            <div className="post-actions">
              {isOwnPost ? (
                <span className="rp-own-post-badge">{t('posts.your_post')}</span>
              ) : (
                <CustomButton7
                  label={t('chat.send_message')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMessageClick(post);
                  }}
                >
                  <FaRegPaperPlane size={18} />
                </CustomButton7>
              )}
            </div>
          }
        >
          <div className="post-card-default-content" onClick={() => handlePostClick(post)}>
            <div className="map-container-static">
              <div 
                className="map-container" 
                onMouseMove={(e) => {
                  e.stopPropagation();
                  handleMouseMove(e, e.currentTarget);
                }}
              >
                <img
                  src={styleStaticMapUrl(post.map_image_url) || '/placeholder-map.jpg'}
                  alt={t('chat.route_map')}
                  className="route-map"
                />
              </div>
            </div>

            <div className="post-content">
              {/* Show route detail only in regular mode */}
              <div className="post-detail">
                <span className="detail-icon"><FaRoute /></span>
                <div className="detail-content">
                  <div className="detail-label">{t('posts.route')}:</div>
                  <div className="location-detail">
                    <span className="location-marker from">S</span>
                    <span className="location-text">
                      {formatCompactAddress(
                        post.address_from_street,
                        post.address_from_city,
                        post.address_from_country
                      )}
                    </span>
                  </div>
                  <div className="location-detail">
                    <span className="location-marker to">E</span>
                    <span className="location-text">
                      {formatCompactAddress(
                        post.address_to_street,
                        post.address_to_city,
                        post.address_to_country
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="post-info-grid">
                <div className="post-detail">
                  <span className="detail-icon"><FaCalendarAlt /></span>
                  <div className="detail-content">
                    <div className="detail-label">{t('posts.date')}:</div>
                    <div>{formatDate(post.travel_date)}</div>
                  </div>
                </div>

                <div className="post-detail">
                  <span className="detail-icon"><FaClock /></span>
                  <div className="detail-content">
                    <div className="detail-label">{t('posts.time')}:</div>
                    <div>{formatTime(post.travel_time)}</div>
                  </div>
                </div>

                <div className="post-detail secondary-info">
                  <span className="detail-icon"><FaBox /></span>
                  <div className="detail-content">
                    <div className="detail-label">Size:</div>
                    <div>{post.package_size}</div>
                  </div>
                </div>

                <div className="post-detail secondary-info">
                  <span className="detail-icon"><FaWeightHanging /></span>
                  <div className="detail-content">
                    <div className="detail-label">Weight:</div>
                    <div>{post.package_weight}</div>
                  </div>
                </div>

                <div className="post-detail secondary-info">
                  <span className="detail-icon"><FaShippingFast /></span>
                  <div className="detail-content">
                    <div className="detail-label">Urgency:</div>
                    <div>{post.delivery_urgency}</div>
                  </div>
                </div>

                {/* Delivery Cost Display */}
                {post.delivery_cost && (
                  <div className="post-detail secondary-info price-info">
                    <span className="detail-icon"><FaEuroSign /></span>
                    <div className="detail-content">
                      <div className="detail-label">{t('posts.delivery_cost_offered')}:</div>
                      <div className="price-value">€{parseFloat(post.delivery_cost).toFixed(2)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </PostCardPagination>
      </div>
      </div>
    );
  };

  const renderRegularPost = (post) => {
    if (viewMode === 'list') return renderListCard(post);
    const isOwnPost = currentUser?.id === post.user_id;
    const isDriver = post.role === 'Driver';

    // Compact mode - no pagination, simple layout
    if (viewMode === 'compact') {
      return (
        <div key={post.id} className="rp-post-frame">
        <div
          className={`rp-post-card rp-compact-view ${isDriver ? 'rp-driver-post' : 'rp-passenger-post'} ${isOwnPost ? 'rp-own-post' : ''}`}
          onClick={() => handlePostClick(post)}
        >
          {/* Simple header */}
          <div className="RP-p-user-name">
            <img
              src={post.profiles?.avatar_url || '/default-avatar.png'}
              alt={post.profiles?.username || t('chat.unknown_user')}
              className="rp-p-avatar"
            />
            <div className="user-info-with-badges">
              {/* User badges - small size for compact view */}
              {post.profiles?.badges?.length > 0 && (
                <div className="user-badges-container compact">
                  <UserTrustChip badges={post.profiles.badges} />
                </div>
              )}
              <h3>{post.profiles?.username || t('chat.unknown_user')}</h3>
            </div>
            <div className="post-badges-row">
              <div className="post-type-badge" data-tooltip={post.role}>
                {post.role === 'Driver' ?
                  <FaCarSide className="badge-icon driver" /> :
                  <FaUser className="badge-icon passenger" />}
              </div>
              {post.travel_type === 'recurring' && (
                <div className="post-type-badge rp-recurring-badge" data-tooltip={t('forms.daily_ride') || 'Recurring'}>
                  <FaRedoAlt className="badge-icon recurring" />
                </div>
              )}
              {/* Price Badge - only show if price exists (not for free rides) */}
              {(post.price || post.delivery_cost) && (
                <div className="post-type-badge price-badge" data-tooltip={post.role === 'Passenger' ? t('posts.price_offered') : post.delivery_cost ? t('posts.delivery_cost_offered') : t('posts.price_asked')}>
                  €{parseFloat(post.price || post.delivery_cost).toFixed(0)}
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="map-container-static">
            <div 
              className="map-container" 
              onMouseMove={(e) => {
                e.stopPropagation();
                handleMouseMove(e, e.currentTarget);
              }}
            >
              <img
                src={styleStaticMapUrl(post.map_image_url) || '/placeholder-map.jpg'}
                alt={t('chat.route_map')}
                className="route-map"
              />
            </div>
          </div>

          {/* Minimal content */}
          <div className="post-content">
            <div className="post-info-grid">
              <div className="post-detail">
                <span className="detail-icon"><FaCalendarAlt /></span>
                <div className="detail-content">
                  <div className="detail-label">{post.travel_type === 'recurring' ? t('recurring.schedule') : t('posts.date')}:</div>
                  <div>{post.travel_type === 'recurring' ? formatRecurrencePattern(post) : formatDate(post.travel_date)}</div>
                </div>
              </div>

              <div className="post-detail">
                <span className="detail-icon"><FaClock /></span>
                <div className="detail-content">
                  <div className="detail-label">{t('posts.time')}:</div>
                  <div>{formatTime(post.travel_time)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Simple footer */}
          <div className="post-actions">
            {isOwnPost ? (
              <span className="rp-own-post-badge">{t('posts.your_post')}</span>
            ) : (
              <CustomButton7
                label={t('chat.send_message')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessageClick(post);
                }}
              >
                <FaRegPaperPlane size={18} />
              </CustomButton7>
            )}
          </div>
        </div>
        </div>
      );
    }

    // Regular mode - with pagination
    return (
      <div key={post.id} className="rp-post-frame">
      <div
        className={`rp-post-card rp-regular-view ${isDriver ? 'rp-driver-post' : 'rp-passenger-post'} ${isOwnPost ? 'rp-own-post' : ''}`}
      >
        <PostCardPagination
          userId={post.user_id}
          username={post.profiles?.username || t('chat.unknown_user')}
          post={post}
          paginated={false}
          headerContent={
            <div className="pagination-header-content" onClick={() => handlePostClick(post)}>
              <div className="RP-p-user-name">
                <img
                  src={post.profiles?.avatar_url || '/default-avatar.png'}
                  alt={post.profiles?.username || t('chat.unknown_user')}
                  className="rp-p-avatar"
                />
                <div className="user-info-with-badges">
                  {/* User badges - medium size for regular view */}
                  {post.profiles?.badges?.length > 0 && (
                    <div className="user-badges-container regular">
                      <UserTrustChip badges={post.profiles.badges} />
                    </div>
                  )}
                  <div className="rp-name-row">
                    <h3>{post.profiles?.username || t('chat.unknown_user')}</h3>
                    {Number(post.profiles?.overall_score) > 0 && (
                      <span className="rp-rating-chip" title={t('user_stats.overall')}>
                        <FaStar /> {Number(post.profiles.overall_score).toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="post-badges-row">
                <div className="post-type-badge" data-tooltip={post.role}>
                  {post.role === 'Driver' ?
                    <FaCarSide className="badge-icon driver" /> :
                    <FaUser className="badge-icon passenger" />}
                </div>
                {post.travel_type === 'recurring' && (
                  <div className="post-type-badge rp-recurring-badge" data-tooltip={t('forms.daily_ride') || 'Recurring'}>
                    <FaRedoAlt className="badge-icon recurring" />
                  </div>
                )}
                {/* Price Badge - only show if price exists (not for free rides) */}
                {(post.price || post.delivery_cost) && (
                  <div className="post-type-badge price-badge" data-tooltip={post.role === 'Passenger' ? t('posts.price_offered') : post.delivery_cost ? t('posts.delivery_cost_offered') : t('posts.price_asked')}>
                    €{parseFloat(post.price || post.delivery_cost).toFixed(0)}
                  </div>
                )}
              </div>
            </div>
          }
          footerContent={
            <div className="post-actions">
              {isOwnPost ? (
                <span className="rp-own-post-badge">{t('posts.your_post')}</span>
              ) : (
                <CustomButton7
                  label={t('chat.send_message')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMessageClick(post);
                  }}
                >
                  <FaRegPaperPlane size={18} />
                </CustomButton7>
              )}
            </div>
          }
        >
          <div className="post-card-default-content" onClick={() => handlePostClick(post)}>
            <div className="map-container-static">
              <div 
                className="map-container" 
                onMouseMove={(e) => {
                  e.stopPropagation();
                  handleMouseMove(e, e.currentTarget);
                }}
              >
                <img
                  src={styleStaticMapUrl(post.map_image_url) || '/placeholder-map.jpg'}
                  alt={t('chat.route_map')}
                  className="route-map"
                />
              </div>
            </div>

            <div className="post-content">
              {/* Show route detail in regular mode */}
              <div className="post-detail">
                <span className="detail-icon"><FaRoute /></span>
                <div className="detail-content">
                  <div className="detail-label">{t('posts.route')}:</div>
                  <div className="location-detail">
                    <span className="location-marker from">S</span>
                    <span className="location-text">
                      {formatCompactAddress(
                        post.address_from_street,
                        post.address_from_city,
                        post.address_from_country
                      )}
                    </span>
                  </div>
                  <div className="location-detail">
                    <span className="location-marker to">E</span>
                    <span className="location-text">
                      {formatCompactAddress(
                        post.address_to_street,
                        post.address_to_city,
                        post.address_to_country
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="post-info-grid">
                <div className="post-detail">
                  <span className="detail-icon"><FaCalendarAlt /></span>
                  <div className="detail-content">
                    <div className="detail-label">{post.travel_type === 'recurring' ? t('recurring.schedule') : t('posts.date')}:</div>
                    <div>{post.travel_type === 'recurring' ? formatRecurrencePattern(post) : formatDate(post.travel_date)}</div>
                  </div>
                </div>

                <div className="post-detail">
                  <span className="detail-icon"><FaClock /></span>
                  <div className="detail-content">
                    <div className="detail-label">{t('posts.time')}:</div>
                    <div>{formatTime(post.travel_time)}</div>
                  </div>
                </div>

                {post.car && (
                  <div className="post-detail secondary-info">
                    <span className="detail-icon"><FaCar /></span>
                    <div className="detail-content">
                      <div className="detail-label">{t('posts.car')}:</div>
                      <div>{post.car}</div>
                    </div>
                  </div>
                )}

                {post.seats && (
                  <div className="post-detail secondary-info">
                    <span className="detail-icon"><FaUserAlt /></span>
                    <div className="detail-content">
                      <div className="detail-label">{t('posts.seats')}:</div>
                      <div>{post.seats}</div>
                    </div>
                  </div>
                )}

                {/* Price Display - Shows Free badge or price */}
                {(post.price || post.is_free) && (
                  <div className="post-detail secondary-info price-info">
                    <span className="detail-icon"><FaEuroSign /></span>
                    <div className="detail-content">
                      <div className="detail-label">
                        {post.role === 'Passenger' ? t('posts.price_offered') : t('posts.price_asked')}:
                      </div>
                      <div className="price-value">
                        {post.is_free ? (
                          <span className="free-badge">{t('posts.free')}</span>
                        ) : (
                          `€${parseFloat(post.price).toFixed(2)}`
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Only show drive type for non-recurring posts */}
                {post.travel_type !== 'recurring' && (
                  <div className="post-detail secondary-info">
                    <span className="detail-icon"><FaRoute /></span>
                    <div className="detail-content">
                      <div className="detail-label">{t('posts.drive_type')}:</div>
                      <div>{post.travel_type ? t(`posts.${post.travel_type.toLowerCase().replace('-', '_')}`) : t('posts.not_specified')}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </PostCardPagination>
      </div>
      </div>
    );
  };

  // Safety timeout for loading state
  useEffect(() => {
    if (loading && !allDataLoaded) {
      const safetyTimeout = setTimeout(() => {
        setLoading(false);
        setAllDataLoaded(true);
        console.warn('RecentPosts: Safety timeout triggered - forcing load completion');
        logPaginationState();
      }, 8000);
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [loading, allDataLoaded]);
  
  // Cleanup loading queue on unmount
  useEffect(() => {
    return () => {
      loadingQueueRef.current = [];
      isLoadingRef.current = false;
    };
  }, []);

  // Debug effect to log state changes
  useEffect(() => {
    logPaginationState();
  }, [displayedPosts, totalAvailablePosts, posts.length, loading, allDataLoaded]);

  return (
    <div className="rp-container">
      {posts.length === 0 && loading ? (
        // Initial loading state - show just a few skeletons
        <div className="rp-posts-grid">
          {[1, 2, 3, 4, 5, 6].map((item, index) => renderSkeletonCard(index))}
        </div>
      ) : posts.length > 0 ? (
        // Progressive loading - show mix of content and skeletons
        <>
          {renderProgressiveContent()}
          
          {/* FIXED Load More Button */}
          {canLoadMore && (
            <div className="load-more-container">
              <CustomButton11
                label={isLoadingMore ? t('posts.loading_more') : t('posts.load_more', { count: Math.min(POSTS_PER_PAGE, totalAvailablePosts - displayedPosts) })}
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="load-more-button"
              >
                {isLoadingMore && <Spinner size="small" showText={false} />}
              </CustomButton11>
              <div className="posts-count-info">
                {t('posts.showing_count', { showing: displayedPosts, total: totalAvailablePosts })}
              </div>
            </div>
          )}
        </>
      ) : allDataLoaded ? (
        // No posts found after loading complete
        <div className="rp-no-posts-message">
          <p>{t('posts.no_active')}</p>
        </div>
      ) : (
        // Fallback loading state
        <div className="rp-posts-grid">
          {[1, 2, 3].map((item, index) => renderSkeletonCard(index))}
        </div>
      )}
      
      {/* Message Confirmation Modal */}
      <MessageConfirmationModal
        isOpen={messageConfirmModal.isOpen}
        onClose={handleCloseMessageModal}
        onConfirm={handleConfirmMessage}
        post={messageConfirmModal.post}
        isLoading={messageConfirmModal.isLoading}
      />

      {/* Date Selection Modal (for recurring posts) */}
      <DateSelectionModal
        isOpen={dateSelectionModal.isOpen}
        onClose={() => setDateSelectionModal({ isOpen: false, post: null })}
        onSelectDate={handleDateSelected}
        post={dateSelectionModal.post}
      />
    </div>
  );
}

export default RecentPosts;